import { useEffect, useState } from 'react'

/**
 * App-mode detection — decides whether the site renders the mobile
 * app shell (<MobileApp/>) instead of the desktop tree.
 *
 * `isAppMode()` = the LAYOUT switch, true when ANY of:
 *   1. Running as an installed PWA  — display-mode: standalone / fullscreen
 *   2. Explicit override            — localStorage 'diaex-app-mode' === '1'
 *      (set via ?app=1 in the URL, cleared via ?app=0)
 *   3. Hosted on the app hostname   — aska-jewels-ai.web.app
 *   4. A phone                     — narrow viewport AND a touch-only pointer
 *
 * (1)–(3) are the INSTALLED-APP context, factored out as `isInstalledApp()`;
 * (4) is the device check `isMobileDevice()`. `isAppMode()` is their
 * OR. The split exists because the app shell is auth-first (signed-out ⇒
 * /welcome), which is right for the installed app but wrong for a first-time
 * mobile-Safari visitor who should see the marketing Landing — App.tsx uses
 * `isInstalledApp()` to tell those two narrow-viewport cases apart.
 *
 * The URL override is SESSION-SCOPED (sessionStorage): a desktop tester who
 * forces the phone shell with ?app=1 keeps it across navigations + reloads
 * within that tab, but it evaporates when the tab closes — so a stray ?app=1
 * link can NEVER trap a real web visitor in the auth-first shell forever
 * (the old behaviour wrote localStorage and did exactly that). ?app=0 clears
 * it, and any leftover legacy localStorage latch is shed on the next load.
 */

const STORAGE_KEY = 'diaex-app-mode'
const APP_HOSTNAME = 'aska-jewels-ai.web.app'
const MOBILE_BREAKPOINT = 768

/**
 * Reads ?app=1 / ?app=0 from the current URL into a SESSION-scoped override,
 * and sheds any legacy persistent latch. Idempotent.
 *
 * - ?app=1  → set the session override (survives reloads within this tab only)
 * - ?app=0  → clear it (both stores)
 * - neither → proactively drop a stale localStorage latch left by the OLD
 *   persist-forever behaviour, so a browser that got stuck in app-mode
 *   releases itself on its very next visit with no ?app=0 needed.
 */
export function applyAppModeOverride(): void {
  if (typeof window === 'undefined') return
  try {
    const app = new URLSearchParams(window.location.search).get('app')
    if (app === '1') {
      window.sessionStorage.setItem(STORAGE_KEY, '1')
      window.localStorage.removeItem(STORAGE_KEY) // never persist past the tab
    } else if (app === '0') {
      window.sessionStorage.removeItem(STORAGE_KEY)
      window.localStorage.removeItem(STORAGE_KEY)
    } else {
      // No explicit override this load — retire any legacy localStorage latch.
      window.localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // storage unavailable (private mode / storage disabled) — the other three
    // signals still work, so swallow silently.
  }
}

/**
 * TRUE ONLY INSIDE THE CAPACITOR NATIVE SHELL — the iOS/Android app. Never a
 * browser, and never an installed PWA.
 *
 * ── WHY THIS IS A FIRST-CLASS EXPORT AND NOT AN INLINE CHECK ──────────────
 * The native shells load the LIVE SITE remotely (`capacitor.config.ts`
 * `server.url`), so a routine `firebase deploy --only hosting` changes what runs
 * inside a build Apple already reviewed. That is fine for a search filter. It is
 * NOT fine for anything that takes money: putting a seat-purchase CTA inside the
 * reviewed App Store build with no store release is an IAP-policy violation
 * shipped by a deploy nobody thought of as a release. Seats are web-only for v1
 * (docs/ORG-ACCOUNTS.md), so every purchase surface has to be able to ask "am I
 * in the app?" and get a straight answer.
 *
 * Until now the only reader of `Capacitor.isNativePlatform()` was PRIVATE, inside
 * `isUnderStatusBar()` below — which also returns true for an installed PWA,
 * because for LAYOUT they are the same case. For a PURCHASE gate they are not: a
 * PWA is a browser and may sell. Anyone reaching for the layout helper to gate a
 * CTA would silently hide it from home-screen web users for no reason; anyone
 * hand-rolling the Capacitor check would have to know it exists. Hence one
 * exported predicate with one meaning. Consumers: `lib/seatPlan.ts`'s
 * `canPurchaseSeats()`.
 *
 * `window.Capacitor` is injected by the native bridge before the app's JS runs,
 * so this is synchronous and safe at module scope. Absent in a browser ⇒ false,
 * which is the answer a plain website should give.
 */
export function isNativeShell(): boolean {
  if (typeof window === 'undefined') return false
  const cap = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  try {
    return cap?.isNativePlatform?.() === true
  } catch {
    // A bridge that throws is not a bridge we should trust to be native.
    return false
  }
}

/**
 * True when we're painting UNDER the OS status bar — i.e. the Capacitor native
 * webview (`contentInset: 'never'`) or an installed standalone PWA. In that case
 * the top chrome MUST clear the notch / Dynamic Island itself.
 *
 * Why this exists: the AppBar pads with `env(safe-area-inset-top)`, but in the
 * Capacitor webview that can resolve to **0**, which drops the back button
 * underneath the Dynamic Island where it can't be tapped at all (reported on
 * device 2026-08-08). We can't rely on env() alone, so `installSafeAreaFloor()`
 * publishes a floored value and the chrome uses that.
 *
 * Deliberately NOT true for plain mobile Safari: there the browser chrome
 * already occupies that space, and a floor would just waste ~44px.
 *
 * NOT a substitute for `isNativeShell()`: this is deliberately ALSO true for an
 * installed PWA. Gating a purchase on it would hide the CTA from home-screen web
 * users for no reason.
 */
export function isUnderStatusBar(): boolean {
  if (typeof window === 'undefined') return false
  if (isNativeShell()) return true
  return !!window.matchMedia?.('(display-mode: standalone)').matches
    || !!window.matchMedia?.('(display-mode: fullscreen)').matches
}

/**
 * Publish `--app-safe-top` on <html> for the top chrome to pad with. Equals
 * env(safe-area-inset-top) normally; when we're under the status bar it's
 * floored at 44px so the back button is reachable even if env() reports 0.
 * Call once at boot.
 */
export function installSafeAreaFloor(): void {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  el.style.setProperty(
    '--app-safe-top',
    isUnderStatusBar()
      ? 'max(env(safe-area-inset-top, 0px), 44px)'
      : 'env(safe-area-inset-top, 0px)',
  )
}

/**
 * TRUE when this is the INSTALLED / dedicated app CONTEXT — as opposed to a
 * plain browser tab that merely happens to be narrow. Three signals, NONE of
 * them viewport:
 *   1. Installed PWA           — display-mode: standalone / fullscreen
 *   2. Explicit app override   — sessionStorage 'diaex-app-mode' === '1' (?app=1,
 *                                tab-scoped so it can't trap a visitor forever)
 *   3. The app hostname        — aska-jewels-ai.web.app (Capacitor loads this)
 *
 * ── WHY THIS IS SEPARATE FROM isAppMode() ────────────────────────────────
 * `isAppMode()` conflates two questions — "should I paint the phone shell?"
 * (a LAYOUT question, correctly yes for any narrow viewport) and "is this the
 * installed, auth-first app?" (a PRODUCT question, only yes for the three
 * signals above). The app shell is auth-first: a signed-out visitor sees only
 * /welcome, never the marketing homepage. That is exactly right INSIDE the
 * installed app, but wrong for someone who just typed thediaex.com into mobile
 * Safari — they should land on the Landing page and be invited to sign up.
 *
 * App.tsx uses this to show the Landing (not the phone shell) to a signed-out
 * mobile-web visitor at "/", while the installed app stays fully auth-first.
 * NOT a substitute for isNativeShell() — this is TRUE for an installed PWA,
 * which is still a browser and may sell; see that predicate for purchase gates.
 *
 * Pure + synchronous so App.tsx can branch before first paint. SSR-safe.
 */
export function isInstalledApp(): boolean {
  if (typeof window === 'undefined') return false
  applyAppModeOverride()

  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  if (window.matchMedia?.('(display-mode: fullscreen)').matches) return true
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY) === '1') return true
  } catch {
    // ignore — storage unavailable
  }
  return window.location.hostname === APP_HOSTNAME
}

/**
 * True when the visitor is on a PHONE — a small, touch-ONLY device. BOTH
 * conditions are required:
 *   • a coarse pointer with no hover — a bare touchscreen, not a mouse/trackpad
 *   • a narrow viewport (< 768px)
 *
 * Requiring the TOUCH capability (not width alone) is the fix for a laptop that
 * fell into the phone shell after the user zoomed in or narrowed the window
 * (reported on a laptop 2026-10-14): zoom shrinks innerWidth, but a
 * mouse/trackpad machine still reports `(hover: hover)` / `(pointer: fine)`, so
 * it now correctly stays on the web shell. A touchscreen laptop likewise has a
 * hovering trackpad, so it stays web too.
 *
 * Requiring the NARROW viewport (not touch alone) keeps WIDE touch tablets
 * (iPad / Android tablet in landscape) on the desktop tree exactly as before —
 * that tree is already built touch-aware for them (see tapTargets.guard.test).
 * So the ONLY behaviour that changes is: zoomed / narrow laptops stay web.
 *
 * Where `matchMedia` is unavailable (SSR / jsdom / ancient engines) it falls
 * back to the width-only heuristic so the answer stays defined.
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  const narrow = window.innerWidth < MOBILE_BREAKPOINT
  try {
    const mq = window.matchMedia?.('(hover: none) and (pointer: coarse)')
    if (mq) return narrow && mq.matches === true
  } catch {
    // matchMedia threw — fall through to width-only.
  }
  return narrow
}

/** True when the mobile app shell should render instead of the desktop tree.
 *  = the installed-app context OR an actual touch device. Kept as the one-stop
 *  layout switch; App.tsx additionally consults isInstalledApp() to decide
 *  whether a touch-device visitor is the auth-first app or just mobile web. */
export function isAppMode(): boolean {
  return isInstalledApp() || isMobileDevice()
}

/**
 * Reactive hook form — re-evaluates on window resize, display-mode changes,
 * and pointer-capability changes (a 2-in-1 docking/undocking flips between
 * coarse+no-hover and fine+hover), so the shell switches live. App.tsx calls
 * this once at the top of the tree.
 */
export function useIsAppMode(): boolean {
  const [active, setActive] = useState<boolean>(() => {
    // Publish --app-safe-top before first paint so the top chrome never renders
    // one frame with the back button under the notch.
    installSafeAreaFloor()
    return isAppMode()
  })

  useEffect(() => {
    const update = () => { installSafeAreaFloor(); setActive(isAppMode()) }
    window.addEventListener('resize', update)
    const mqs = [
      window.matchMedia?.('(display-mode: standalone)'),
      window.matchMedia?.('(hover: none) and (pointer: coarse)'),
    ]
    for (const mq of mqs) mq?.addEventListener?.('change', update)
    return () => {
      window.removeEventListener('resize', update)
      for (const mq of mqs) mq?.removeEventListener?.('change', update)
    }
  }, [])

  return active
}
